import { postDeliveryEditWindowInHours, confirmationWindowLengthInHours } from '@sporkbytes/config';
import { isWithinHours, getValidDate, isDateValid } from '@sporkbytes/time-utils';
import { isPast } from 'date-fns';
import { isAdmin } from '@sporkbytes/auth-utils';
import { isApproved, isConfirmed, isNotApproved, isNotConfirmed } from '@sporkbytes/object-utils';
import * as mealOptionUtils from '@sporkbytes/meal-option-utils';
import { partnersHaveApproved as partnersHaveApproved$1, getClientApproved as getClientApproved$1, getFeeDiscountActual as getFeeDiscountActual$1, getIncludedContacts as getIncludedContacts$1, totalClientPays, partnersHaveConfirmed as partnersHaveConfirmed$1 } from '@sporkbytes/meal-option-utils';
import { hasPickedUp, totalReceivedByPartner as totalReceivedByPartner$1 } from '@sporkbytes/purchase-order-utils';
import { roundCurrency, getPercent } from '@sporkbytes/math-utils';
import Money from '@sporkbytes/money';
import uniqBy from 'lodash.uniqby';
import maxBy from 'lodash.maxby';

/**
 * @description Given a `mealProposals` array, retrieve the total head count.
 * @param {array} mealProposals - The array of meal proposals for which you retrieve the total head count.
 * @return {number} The total head count for the provided `mealProposals` array.
 */
function headCount(mealProposals) {
  if (Array.isArray(mealProposals)) {
    if (mealProposals.every(function (mealProposal) {
      return mealProposal.headCount == null;
    })) {
      return null;
    }
    return mealProposals.filter(function (mealProposal) {
      return typeof mealProposal.headCount === 'number';
    }).reduce(function (totalHeadCount, mealProposal) {
      return totalHeadCount + mealProposal.headCount;
    }, 0);
  }
  return mealProposals.headCount;
}

/**
 * @description Given a `mealProposals` array, retrieve the average head count.
 * @param {array} mealProposals - The array of meal proposals for which you retrieve the average head count.
 * @return {number} The average head count for the provided `mealProposals` array.
 */
function averageHeadCount(mealProposals) {
  return Math.round(headCount(mealProposals) / mealProposals.length);
}

/**
 * @description Given a meal proposal, determine if delivery drivers can be assigned to it.
 * @param {object} mealProposal - The meal proposal to determine if delivery drivers can be assigned to it.
 * @return {boolean} Whether the given meal proposal can have delivery drivers assigned to it.
 */
function canAssignDeliveryDrivers(mealProposal) {
  return ['In Progress', 'Delivered'].includes(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status) && isWithinHours(new Date(), mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate, postDeliveryEditWindowInHours, true);
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function mealProposalInCorrectState(mealProposal) {
  var additionalStatuses = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return !isPast(getValidDate(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate)) && ['In Progress'].concat(_toConsumableArray(additionalStatuses)).includes(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status);
}

/**
 * @description Given a meal proposal, determine if it can be canceled.
 * @param {object} mealProposal - The meal proposal to determine if it can be canceled.
 * @return {boolean} Whether the given meal proposal can be canceled.
 */
function canCancel(mealProposal) {
  return mealProposalInCorrectState(mealProposal, ['Draft']);
}

/**
 * @description Given `mealProposal`, determine if it has been approved by the client or not.
 * @param {object} mealProposal - The meal proposal to be determined as approved by the client or not.
 * @return {boolean} Whether the given meal proposal has been approved by the client or not.
 */
function clientHasApproved(mealProposal) {
  if (Array.isArray(mealProposal.MealOptions)) {
    return mealProposal.MealOptions.some(isApproved);
  }
  return false;
}

/**
 * @description Given a meal proposal, determine if it can be delivered.
 * @param {object} mealProposal - The meal proposal to determine if it can be delivered.
 * @param {string} role - The role of the user who would like to edit the meal proposal.
 * @return {boolean} Whether the given meal proposal can be delivered.
 */
function canDeliver(mealProposal, role) {
  var hasValidStatus = ['In Progress'].includes(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status);
  var isWithinPostDeliveryEditWindow = isWithinHours(new Date(), mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate, postDeliveryEditWindowInHours, true) && isPast(getValidDate(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate));
  return hasValidStatus && clientHasApproved(mealProposal) && (isWithinPostDeliveryEditWindow || isAdmin(role));
}

/**
 * @description Given a meal proposal, determine if it can be edited.
 * @param {object} mealProposal - The meal proposal to determine if it can be edited.
 * @param {string} role - The role of the user who would like to edit the meal proposal.
 * @return {boolean} Whether the given meal proposal can be edited.
 */
function canEdit(mealProposal, role) {
  var hasValidStatus = ['Delivered', 'Draft', 'In Progress'].includes(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.status);
  var isWithinPostDeliveryEditWindow = isWithinHours(new Date(), mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate, postDeliveryEditWindowInHours, true);
  return hasValidStatus && (isWithinPostDeliveryEditWindow || isAdmin(role));
}

/**
 * @description Given a `mealProposal` object, retrieve the included client contacts.
 * @param {object} mealProposal - The meal proposal object from which to retrieve the included contacts from the client location.
 * @return {array} The array of included contacts.
 */
function getIncludedClientContacts(mealProposal) {
  if (Array.isArray(mealProposal.MealProposalsContacts)) {
    return mealProposal.MealProposalsContacts.map(function (_ref) {
      var Contact = _ref.Contact;
      return Contact;
    });
  } else if (Array.isArray(mealProposal.Contacts)) {
    return mealProposal.Contacts;
  }
  return [];
}

/**
 * @description Given a `mealProposal` object, determine if it has any included client contacts.
 * @param {object} mealProposal - The meal proposal object from which to determine if there are any included client contacts.
 * @return {array} Whether the `mealProposal` has any client contacts.
 */
function hasIncludedClientContacts(mealProposal) {
  return getIncludedClientContacts(mealProposal).length > 0;
}

/**
 * @description Given a meal proposal, determine if approval can be requested from the client.
 * @param {object} mealProposal - The meal proposal to determine if approval can be requested from the client.
 * @return {boolean} Whether approval for the given meal proposal can be requested from the client.
 */
function canRequestClientApproval(mealProposal) {
  return !clientHasApproved(mealProposal) && mealProposalInCorrectState(mealProposal) && hasIncludedClientContacts(mealProposal);
}

/**
 * @description Given `mealProposal`, determine if it has been confirmed by the client or not.
 * @param {object} mealProposal - The meal proposal to be determined as confirmed by the client or not.
 * @return {boolean} Whether the given meal proposal has been confirmed by the client or not.
 */
function clientHasConfirmed(mealProposal) {
  if (Array.isArray(mealProposal.MealOptions)) {
    return mealProposal.MealOptions.some(isConfirmed);
  }
  return false;
}

/**
 * @description Given `mealProposal`, determine if it has been approved by the partners or not.
 * @param {object} mealProposal - The meal proposal to be determined as approved by the partners or not.
 * @return {boolean} Whether the given meal proposal has been approved by the partners or not.
 */
function partnersHaveApproved(mealProposal) {
  if (Array.isArray(mealProposal.MealOptions) && clientHasApproved(mealProposal)) {
    return partnersHaveApproved$1(getClientApproved$1(mealProposal.MealOptions));
  }
  return false;
}

/**
 * @description Given a meal proposal, determine if confirmation can be requested from the client.
 * @param {object} mealProposal - The meal proposal to determine if confirmation can be requested from the client.
 * @return {boolean} Whether confirmation for the given meal proposal can be requested from the client.
 */
function canRequestClientConfirmation(mealProposal) {
  return clientHasApproved(mealProposal) && !clientHasConfirmed(mealProposal) && partnersHaveApproved(mealProposal) && mealProposalInCorrectState(mealProposal) && isWithinHours(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate, new Date(), confirmationWindowLengthInHours.client, true) && hasIncludedClientContacts(mealProposal);
}

/**
 * @description Given a meal proposal, determine if approval can be requested from the partners.
 * @param {object} mealProposal - The meal proposal to determine if approval can be requested from the partners.
 * @return {boolean} Whether approval for the given meal proposal can be requested from the partners.
 */
function canRequestPartnerApproval(mealProposal) {
  var approvedMealOption = getClientApproved$1(mealProposal.MealOptions);
  return !!approvedMealOption && approvedMealOption.PurchaseOrders.some(isNotApproved) && mealProposalInCorrectState(mealProposal);
}

/**
 * @description Given a meal proposal, determine if confirmation can be requested from the partners.
 * @param {object} mealProposal - The meal proposal to determine if confirmation can be requested from the partners.
 * @return {boolean} Whether confirmation for the given meal proposal can be requested from the partners.
 */
function canRequestPartnerConfirmation(mealProposal) {
  var approvedMealOption = getClientApproved$1(mealProposal.MealOptions);
  return !!approvedMealOption && approvedMealOption.PurchaseOrders.every(isApproved) && approvedMealOption.PurchaseOrders.some(isNotConfirmed) && mealProposalInCorrectState(mealProposal) && isWithinHours(mealProposal === null || mealProposal === void 0 ? void 0 : mealProposal.deliveryDate, new Date(), confirmationWindowLengthInHours.partner, true);
}

/**
 * Given a meal proposal, determine if the delivery has started.
 * @param {object} mealProposal - A meal proposal to determine if its delivery has started.
 * @return {boolean} Whether the given meal proposal's delivery has started.
 */
function hasDeliveryStarted(mealProposal) {
  var _getClientApproved;
  var _ref = (_getClientApproved = getClientApproved$1(mealProposal.MealOptions)) !== null && _getClientApproved !== void 0 ? _getClientApproved : {
      PurchaseOrders: []
    },
    PurchaseOrders = _ref.PurchaseOrders;
  var pickupTimeInThePast = PurchaseOrders.some(function (_ref2) {
    var pickupTime = _ref2.pickupTime;
    return isPast(getValidDate(pickupTime));
  });
  var hasBeenPickedUp = PurchaseOrders.some(hasPickedUp);
  return pickupTimeInThePast || hasBeenPickedUp;
}

/**
 * Given a meal proposal, determine if we can reset the approvals and confirmations of its meal options and purchase orders.
 * @param {object} mealProposal - A meal proposal to determine if we can reset approvals and confirmations.
 * @return {boolean} Whether the given meal proposal's meal options and purchase orders can have their approvals and confirmations reset.
 */
function canResetApprovalsAndConfirmations(mealProposal) {
  return mealProposalInCorrectState(mealProposal, ['Draft']) && !hasDeliveryStarted(mealProposal);
}

/**
 * @description Given a `calculationName` and one or more `mealProposals`, calculate the provided `calculationName` for the given `mealProposals`.
 * @param {string} calculationName - The name of the calculation you're calculating.  Valid options are `totalClientPays`, `totalReceivedByAllPartners`, and `totalReceivedBySpork`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are calculating the provided `calculationName`.
 * @return {number} The calculated amount for the given meal proposal(s).
 */
function getSummationCalculation(calculationName, mealProposals) {
  if (Array.isArray(mealProposals)) {
    return +mealProposals.flatMap(function (mealProposal) {
      return getSummationCalculation(calculationName, mealProposal);
    }).reduce(function (sum, value) {
      return sum.add(value);
    }, new Money(0));
  }
  if (clientHasApproved(mealProposals)) {
    var approvedMealOption = mealOptionUtils.getClientApproved(mealProposals.MealOptions);
    var MealProposalFees = mealProposals.MealProposalFees,
      headCount = mealProposals.headCount;
    return mealOptionUtils[calculationName](approvedMealOption, {
      MealProposalFees: MealProposalFees,
      headCount: headCount
    });
  }
  return 0;
}

/**
 * @description Given one or more `mealProposals`, calculate the cost per person for the given `mealProposals`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are calculating the cost per person.
 * @return {number} The cost per person for the provided meal proposal(s).
 */
function costPerPerson(mealProposals) {
  var totalClientPays = getSummationCalculation('totalClientPays', mealProposals);
  var totalHeadCount = headCount(mealProposals);
  return roundCurrency(totalClientPays / totalHeadCount);
}

/**
 * @description Given `mealProposals`, retrieve the meal proposals that have been approved by the client.
 * @param {array} mealProposals - The array of meal proposals for which you retrieve those that have been approved by the client.
 * @return {array} The meal proposals that have been approved by the client.
 */
function getClientApproved(mealProposals) {
  if (Array.isArray(mealProposals)) {
    return mealProposals.filter(clientHasApproved);
  }
  return [];
}

/**
 * @description Given a `FeeId` and `mealProposals`, calculate the aggregated discount amount for the given `mealProposals` and `FeeId`.
 * @param {string} FeeId - The ID of the fee whose discount you're calculating.
 * @param {object} mealProposals - The meal proposals for which we calculate the given fee discount.
 * @return {number} The aggregated amount the fee is discounted.
 */
function getFeeDiscountActual(FeeId, mealProposals) {
  if (Array.isArray(mealProposals)) {
    return +mealProposals.reduce(function (sum, mealProposal) {
      return sum.add(getFeeDiscountActual(FeeId, mealProposal));
    }, new Money(0));
  }
  var approvedMealOption = getClientApproved$1(mealProposals.MealOptions);
  if (!approvedMealOption) {
    return 0;
  }
  var MealProposalFees = mealProposals.MealProposalFees,
    headCount = mealProposals.headCount;
  var foundFees = MealProposalFees.filter(function (fee) {
    return fee.FeeId === FeeId;
  });
  return +foundFees.reduce(function (total, fee) {
    return total.add(getFeeDiscountActual$1(fee, approvedMealOption, headCount));
  }, new Money(0));
}

/**
 * @description Given a `mealProposal` object, retrieve the included contacts' IDs from the meal proposal's client location.
 * @param {object} purchaseOrder - The meal proposal object from which to retrieve the included contacts' IDs from the client location.
 * @return {array} The array of included contacts' IDs.
 */
function getIncludedClientContactIds(mealProposal) {
  return getIncludedClientContacts(mealProposal).map(function (contact) {
    return contact.id;
  });
}

/**
 * @description Given a `mealProposal` object, retrieve the included contacts from the meal proposal's client location and meal options' partner locations.
 * @param {object} mealProposal - The meal proposal object from which to retrieve the included contacts from the client location and meal options' partner locations.
 * @return {object} an object with keys `clientContacts`, which contains an array of the client contacts, and `partnerContacts` which contains an array of the partner contacts.
 */
function getIncludedContacts(mealProposal) {
  var partnerContacts = [];
  if (Array.isArray(mealProposal.MealOptions)) {
    partnerContacts = mealProposal.MealOptions.flatMap(getIncludedContacts$1);
  }
  return {
    clientContacts: getIncludedClientContacts(mealProposal),
    partnerContacts: uniqBy(partnerContacts, function (contact) {
      return contact.id;
    })
  };
}

/**
 * @description Given a `mealProposal` object, retrieve the included contacts' IDs from the meal proposal's client location and meal options' partner locations.
 * @param {object} mealProposal - The meal proposal object from which to retrieve the included contacts' IDs from the client location and meal options' partner locations.
 * @return {array} The array of included contacts' IDs.
 */
function getIncludedContactIds(mealProposal) {
  var _getIncludedContacts = getIncludedContacts(mealProposal),
    clientContacts = _getIncludedContacts.clientContacts,
    partnerContacts = _getIncludedContacts.partnerContacts;
  return [].concat(_toConsumableArray(clientContacts.map(function (_ref) {
    var id = _ref.id;
    return id;
  })), _toConsumableArray(partnerContacts.map(function (_ref2) {
    var id = _ref2.id;
    return id;
  })));
}

/**
 * @description Given a `calculationName` and one or more `mealProposals`, calculate the provided `calculationName` per meal for the given `mealProposals`.
 * @param {string} calculationName - The name of the calculation you're calculating.  Valid options are `totalClientPays`, `totalReceivedByAllPartners`, and `totalReceivedBySpork`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are calculating the provided `calculationName` per meal.
 * @return {number} The per meal calculated amount for the given meal proposal(s).
 */
function getPerMealCalculation(calculationName, mealProposals) {
  var summationCalculation = getSummationCalculation(calculationName, mealProposals);
  var mealProposalCount = Array.isArray(mealProposals) ? getClientApproved(mealProposals).length : 1;
  return roundCurrency(summationCalculation / mealProposalCount);
}

/**
 * @description Given a one or more `mealProposals`, calculate the potential amount of revenue for any unapproved `mealProposals`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are calculating the potential revenue.
 * @return {number} The potential revenue calculated for the unapproved given meal proposal(s).
 */
function getPotentialRevenue(mealProposals) {
  if (!mealProposals) {
    return 0;
  }
  if (Array.isArray(mealProposals)) {
    return +mealProposals.filter(function (mealProposal) {
      return !clientHasApproved(mealProposal);
    }).map(function (mealProposal) {
      return getPotentialRevenue(mealProposal);
    }).reduce(function (sum, value) {
      return sum.add(value);
    }, new Money(0));
  }
  if (clientHasApproved(mealProposals)) {
    return 0;
  }
  var MealProposalFees = mealProposals.MealProposalFees,
    headCount = mealProposals.headCount;
  return +new Money(totalClientPays(maxBy(mealProposals.MealOptions, function (mealOption) {
    return totalClientPays(mealOption, {
      MealProposalFees: MealProposalFees,
      headCount: headCount
    });
  }), {
    MealProposalFees: MealProposalFees,
    headCount: headCount
  }));
}

/**
 * @description Given one or more `mealProposals`, return the purchase orders for `PartnerId`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are finding purchase orders for the partner.
 * @param {string} PartnerId - The PartnerId of the purchase orders.
 * @return {object|array} The purchase orders that belong to the partner with PartnerId.
 */
function getPurchaseOrders(mealProposals, PartnerId) {
  if (Array.isArray(mealProposals)) {
    return mealProposals.reduce(function (purchaseOrders, mealProposal) {
      var purchaseOrder = getPurchaseOrders(mealProposal, PartnerId);
      if (!purchaseOrder) {
        return purchaseOrders;
      }
      return [].concat(_toConsumableArray(purchaseOrders), [purchaseOrder]);
    }, []);
  }
  var approvedMealOption = getClientApproved$1(mealProposals.MealOptions);
  if (!approvedMealOption) {
    return null;
  }
  var purchaseOrder = approvedMealOption.PurchaseOrders.find(function (purchaseOrder) {
    return purchaseOrder.PartnerLocation.Partner.id === PartnerId;
  });
  if (!purchaseOrder) {
    return null;
  }
  return purchaseOrder;
}

/**
 * @description Given one or more `mealProposals`, calculate Spork's gross margin for the given `mealProposals`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are calculating Spork's gross margin.
 * @return {number} Spork's gross margin for the provided meal proposal(s).
 */
function grossMargin(mealProposals) {
  var totalClientPays = getSummationCalculation('totalClientPays', mealProposals);
  var totalReceivedBySpork = getSummationCalculation('totalReceivedBySpork', mealProposals);
  return getPercent(totalReceivedBySpork, totalClientPays) || 0;
}

/**
 * @description Given a `mealProposal`, return whether or not the driver has arrived at the client location.
 * @param {object} mealProposal - The meal proposal to check if the driver has arrived at the client location.
 * @return {boolean} Whether or not the driver has arrived at the client location .
 */
function hasArrivedAtClientLocation(mealProposal) {
  var arrivedAt = mealProposal.arrivedAt;
  return !!arrivedAt && isDateValid(arrivedAt);
}

/**
 * @description Given a `mealProposal`, return whether or not the driver has left the client location.
 * @param {object} mealProposal - The meal proposal to check if the driver has left the client location.
 * @return {boolean} Whether or not the driver has left the client location.
 */
function hasLeftClientLocation(mealProposal) {
  var leftAt = mealProposal.leftAt;
  return !!leftAt && isDateValid(leftAt);
}

/**
 * @description Given a `mealProposal`, return whether or not the driver has picked up the delivery gear.
 * @param {object} mealProposal - The meal proposal to check if the driver has picked up the delivery gear.
 * @return {boolean} Whether or not the driver has picked up the delivery gear.
 */
function hasPickedUpDeliveryGear(mealProposal) {
  var deliveryGearPickedUpAt = mealProposal.deliveryGearPickedUpAt;
  return !!deliveryGearPickedUpAt && isDateValid(deliveryGearPickedUpAt);
}

/**
 * @description Given `mealProposal`, determine if it has been canceled or not.
 * @param {object} mealProposal - The meal proposal to be determined as canceled or not.
 * @return {boolean} Whether the given meal proposal is canceled or not.
 */
function isCanceled(mealProposal) {
  return mealProposal.status === 'Canceled';
}

/**
 * @description Given `mealProposal`, determine if it has been delivered or not.
 * @param {object} mealProposal - The meal proposal to be determined as delivered or not.
 * @return {boolean} Whether the given meal proposal is delivered or not.
 */
function isDelivered(mealProposal) {
  return mealProposal.status === 'Delivered';
}

/**
 * @description Given `mealProposal`, determine if it is a draft or not.
 * @param {object} mealProposal - The meal proposal to be determined as a draft or not.
 * @return {boolean} Whether the given meal proposal is a draft or not.
 */
function isDraft(mealProposal) {
  return mealProposal.status === 'Draft';
}

/**
 * @description Given `mealProposal`, determine if it has been invoiced or not.
 * @param {object} mealProposal - The meal proposal to be determined as invoiced or not.
 * @return {boolean} Whether the given meal proposal is invoiced or not.
 */
function isInvoiced(mealProposal) {
  return !!mealProposal.InvoiceId;
}

/**
 * @description Given `mealProposal`, determine if it has been confirmed by the partners or not.
 * @param {object} mealProposal - The meal proposal to be determined as confirmed by the partners or not.
 * @return {boolean} Whether the given meal proposal has been confirmed by the partners or not.
 */
function partnersHaveConfirmed(mealProposal) {
  if (Array.isArray(mealProposal.MealOptions) && clientHasApproved(mealProposal)) {
    return partnersHaveConfirmed$1(getClientApproved$1(mealProposal.MealOptions));
  }
  return false;
}

/**
 * @description Given one or more `mealProposals` and a `PartnerId`, calculate the total received by that partner for all approved `mealProposals`.
 * @param {object|array} mealProposals - The meal proposal(s) for which you are calculating the total received by the partner.
 * @param {string} PartnerId -  The id of the partner for which you are calculating the total received.
 * @return {number} The total received by the partner for the given meal proposal(s).
 */
function totalReceivedByPartner(mealProposals, PartnerId) {
  if (Array.isArray(mealProposals)) {
    return +mealProposals.reduce(function (sum, mealProposal) {
      return sum.add(totalReceivedByPartner(mealProposal, PartnerId));
    }, new Money(0));
  }
  var purchaseOrder = getPurchaseOrders(mealProposals, PartnerId);
  if (!purchaseOrder) {
    return 0;
  }
  return totalReceivedByPartner$1(purchaseOrder);
}

export { averageHeadCount, canAssignDeliveryDrivers, canCancel, canDeliver, canEdit, canRequestClientApproval, canRequestClientConfirmation, canRequestPartnerApproval, canRequestPartnerConfirmation, canResetApprovalsAndConfirmations, clientHasApproved, clientHasConfirmed, costPerPerson, getClientApproved, getFeeDiscountActual, getIncludedClientContactIds, getIncludedClientContacts, getIncludedContactIds, getIncludedContacts, getPerMealCalculation, getPotentialRevenue, getPurchaseOrders, getSummationCalculation, grossMargin, hasArrivedAtClientLocation, hasDeliveryStarted, hasIncludedClientContacts, hasLeftClientLocation, hasPickedUpDeliveryGear, headCount, isCanceled, isDelivered, isDraft, isInvoiced, partnersHaveApproved, partnersHaveConfirmed, totalReceivedByPartner };
